import React, { useContext, useEffect, useState, ReactElement } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { ShieldX, ArrowLeft, LayoutDashboard } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

const AccessDenied: React.FC<{ role?: string }> = ({ role }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
            <ShieldX className="text-red-500" size={30} />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-bold text-gray-800">Access Restricted</h1>
          <p className="text-sm text-gray-500">
            You don't have permission to access this module.
          </p>
          <p className="text-sm text-gray-400">
            If you believe this is a mistake, please contact your Tenant Administrator.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-300 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={15} />
            Go Back
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="btn-primary flex-1"
          >
            <LayoutDashboard size={15} />
            Dashboard
          </button>
        </div>
        {role && (
          <p className="text-xs text-gray-400">
            Logged in as: <span className="font-medium">{role}</span>
          </p>
        )}
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children, roles }: ProtectedRouteProps): ReactElement | null => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500 text-sm">Checking authentication…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && user && !roles.includes(user.role)) {
    return <AccessDenied role={user.role} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
